import { useCallback, useEffect, useState } from 'react';
import { AddonFeatureManager, AuthenticationManager } from 'core';
import i18n from 'i18n';
import _ from 'lodash';
import { DefaultOrderManager, OrderManager } from 'core/order/OrderManager';
import { Order, State } from 'core/order/Order';
import { useCallAPI } from 'hooks/useCallAPI';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { toast } from 'react-toastify';
import {
  DefaultStoredValueManager,
  StoredValueManager,
} from 'core/storedValue/StoredValueManager';

const orderManager: OrderManager = new DefaultOrderManager();
const storedValueManager: StoredValueManager = new DefaultStoredValueManager();

export const useCheckRemainingStoredValue = (
  order: Order,
  addonFeatureManager: AddonFeatureManager,
) => {
  const [remainingStoredValueError, setRemainingStoredValueError] = useState<
    string | undefined
  >(undefined);
  useEffect(() => {
    const checkRemainingStoredValue = async (order: Order) => {
      const error = await validateBudgetWithRemainingStoredValue(
        order,
        addonFeatureManager,
        storedValueManager,
        orderManager,
      );
      if (error) setRemainingStoredValueError(error);
    };
    checkRemainingStoredValue(order);
  }, [order, addonFeatureManager]);

  return {
    remainingStoredValueError,
  };
};

export const useEditOrder = (
  order: Order,
  authenticationManager: AuthenticationManager,
) => {
  return {
    canEditOrder: orderManager.canOrderBeEdit(
      authenticationManager.actor,
      order,
    ),
  };
};

export const useApprove = (order: Order, afterApproval = () => {}) => {
  const { loading, callAPIsBySequence } = useCallAPI();

  const approveOrder = useCallback(async () => {
    callAPIsBySequence(
      [() => orderManager.approveOrders([order.id]), afterApproval],
      () => {
        toast.success(i18n.t<string>('orderDetail.labels.approveSuccess'));
      },
    );
  }, [callAPIsBySequence, order, afterApproval]);

  return {
    showApproveButton: orderManager.canOrderBeApproved(order.state),
    approveLoading: loading,
    approveOrder,
  };
};

export const useReject = (order: Order, afterRejection = () => {}) => {
  const { loading, callAPIsBySequence } = useCallAPI();

  const rejectOrder = useCallback(async () => {
    callAPIsBySequence(
      [() => orderManager.rejectOrders([order.id]), afterRejection],
      () => {
        toast.success(i18n.t<string>('orderDetail.labels.rejectSuccess'));
      },
    );
  }, [callAPIsBySequence, order, afterRejection]);

  return {
    showRejectButton: orderManager.canOrderBeRejected(order.state),
    rejectLoading: loading,
    rejectOrder,
  };
};

export const useSettle = (
  order: Order,
  afterSettlement = () => {},
  addonFeatureManager: AddonFeatureManager,
) => {
  const { loading, callAPIsBySequence } = useCallAPI();

  const settleOrder = useCallback(async () => {
    callAPIsBySequence(
      [() => orderManager.settleOrder(order.id), afterSettlement],
      () => {
        toast.success(i18n.t<string>('orderDetail.labels.settleSuccess'));
      },
    );
  }, [callAPIsBySequence, order, afterSettlement]);

  return {
    canSettle: orderManager.canOrderBeSettled(order.state),
    settleTips: getSettleTips(order.state),
    showSettleButton: showSettleButton(order.state, addonFeatureManager),
    settleLoading: loading,
    settleOrder,
  };
};

const getSettleTips = (orderState: State): string => {
  if (orderState === State.SETTLE) {
    return i18n.t<string>('orderDetail.labels.settleWait');
  }

  if (orderState === State.SETTLED) {
    return i18n.t<string>('orderDetail.labels.settledNoNeedSettle');
  }

  return '';
};

const showSettleButton = (
  orderState: State,
  addonFeatureManager: AddonFeatureManager,
): boolean => {
  const permissionAllowOrderSettlement = addonFeatureManager.isFeatureEnable(
    ADDONFEATURE.COMPANY.AGENCY_ALLOW_ORDER_SETTLEMENT,
  );
  const stateAllowSettlement = ![
    State.CHANGE_PENDING,
    State.NOT_APPROVE,
    State.REJECT,
  ].includes(orderState);
  return permissionAllowOrderSettlement && stateAllowSettlement;
};

const validateBudgetWithRemainingStoredValue = async (
  order: Order,
  addonFeatureManager: AddonFeatureManager,
  storedValueManager: StoredValueManager,
  orderManager: OrderManager,
): Promise<string | undefined> => {
  if (
    addonFeatureManager.isFeatureEnable(ADDONFEATURE.COMPANY.STORED_VALUE) &&
    orderManager.canOrderBeApproved(order.state)
  ) {
    const remainingStoredValue =
      await storedValueManager.getRemainingStoredValue(order.agencyId);
    const budgetToCompare = !_.isNil(order.changeBudget)
      ? order.changeBudget - order.budget
      : order.budget;
    if (
      !_.isNil(remainingStoredValue) &&
      budgetToCompare > remainingStoredValue
    ) {
      return i18n.t<string>('orderDetail.errors.budgetExceedStoredValueError', {
        amount: formatPriceWithCurrency(order.currency, remainingStoredValue),
      });
    }
  }
};
